<script setup lang="ts">
import type { Route } from '@/@types/gtfs-ressource';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import Common from '@/libs/common';
import { collapseDrawer, expandDrawer, releaseDrawerHeight } from '@/libs/helpers/drawer-transition';
import { AppRoute } from '@/libs/routing';
import type { TimetableGroup } from './types';

defineProps<{
  group: TimetableGroup;
  open: boolean;
  /** Absent when the stop_times reference a route missing from the GTFS payload. */
  route?: Route;
}>();

const emit = defineEmits<{ toggle: [] }>();
</script>

<template>
  <section class="route-card">
    <button :aria-expanded="open" class="route-card__header" type="button" @click="emit('toggle')">
      <span class="route-card__identity">
        <RouteBadge v-if="route" :route="route" />
        <span class="route-card__headsign">{{ group.tripHeadsign }}</span>
      </span>
      <BaseIcon
        name="chevron-down"
        class="route-card__chevron"
        :class="{ 'route-card__chevron--open': open }"
      />
    </button>

    <Transition
      name="route-card-panel"
      @enter="expandDrawer"
      @afterEnter="releaseDrawerHeight"
      @leave="collapseDrawer"
    >
      <div v-if="open" class="route-card__panel">
        <ul class="route-card__times">
          <li v-for="stopTime in group.stopTimes" :key="`${stopTime.trip_id}_${stopTime.stop_sequence}`">
            <router-link
              class="route-card__time"
              :to="{ name: AppRoute.TRIP_DETAILED, params: { tripId: stopTime.trip_id } }"
            >
              {{ Common.timestampFormatHHMM(stopTime.departure_ts) }}
            </router-link>
          </li>
        </ul>
      </div>
    </Transition>
  </section>
</template>

<style lang="scss">
.route-card {
  background-color: $color-neutral-lower;
  border-radius: $radius-2xl;
  padding: $spacing-4;

  &__header {
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    gap: $spacing-5;
    padding: 0;
    text-align: left;
    width: 100%;
  }

  &__identity {
    align-items: center;
    display: flex;
    flex: 1;
    gap: 10px;
    min-width: 0;
  }

  // A very long trip_headsign wraps onto 2 lines, then clips
  &__headsign {
    color: $color-neutral-highest;
    display: -webkit-box;
    font: $text-bold-medium;
    letter-spacing: $text-bold-medium-spacing;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  &__chevron {
    color: $color-neutral-highest;
    flex-shrink: 0;
    font-size: 24px;
    transition: transform $duration-drawer-open $easing-drawer;

    &--open {
      transform: rotate(180deg);
    }
  }

  // What animates: with no padding of its own it collapses to exactly 0, and
  // the 4px of `__times` stay inside it.
  &__panel {
    overflow: hidden;
  }

  &__times {
    display: flex;
    flex-direction: column;
    // The departures carry their own vertical padding, so the offset from the
    // header is what is left to reach the mockup's 14px: it only exists while
    // the card is open, and never pushes the collapsed card taller.
    padding-top: 4px;
  }

  // A mistap opens another trip entirely, so each departure needs a full tap
  // target: 10px + 24px of line-height + 10px. The rows sit 44px apart instead
  // of the mockup's 32px — the typography is untouched, the list is just taller.
  &__time {
    color: $color-neutral-highest;
    display: block;
    font: $text-medium;
    letter-spacing: $text-medium-spacing;
    padding: 10px 0;
  }
}

.route-card-panel-enter-active {
  transition:
    height $duration-drawer-open $easing-drawer,
    opacity $duration-drawer-open $easing-drawer;
}

.route-card-panel-leave-active {
  transition:
    height $duration-drawer-close $easing-drawer,
    opacity $duration-drawer-close $easing-drawer;
}

.route-card-panel-enter-from,
.route-card-panel-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .route-card__chevron,
  .route-card-panel-enter-active,
  .route-card-panel-leave-active {
    transition: none;
  }
}
</style>
